import { useCallback, useEffect, useRef, useState } from "react";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import { VadController } from "../audio/vadController";
import { makeSttClient } from "../api/sttClientFactory";
import type { StreamingSttClient } from "../api/streamingSttClient";
import { SilenceTimer } from "../audio/silenceTimer";
import { SHORT_UTTERANCE_SAMPLES_16K } from "../audio/bargeInBridge";
import type { TtsPlayer } from "../audio/ttsPlayer";
import { useChatStore } from "../state/chatStore";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { dlog } from "../observability/debugLogger";

// continuous mode 統合 hook (spec §3)。
// mic (persistent) → VAD → Scribe streaming の pipeline を管理。
// mediaStreamManager が唯一の stream owner、VadController が発話境界を検出、
// StreamingScribeClient が partial/committed transcript を配送、SilenceTimer が
// 無音時の escalation を発火する。barge-in は onSpeechStart で ttsPlayer.stop() を呼ぶ形で実装。
export type VadUiState = "idle" | "listening" | "hearing" | "ai-speaking" | "processing";

// STT realtime の背景音フィルタ (ticket 2026-07-21)。既定 false。
// 関数内で読むのはテストの vi.stubEnv を効かせるため (module-top const だと
// stub より先に評価されてしまう)。
function readFilterBackgroundAudioFromEnv(): boolean {
  return import.meta.env.VITE_STT_FILTER_BACKGROUND_AUDIO === "true";
}

type ContinuousModeError =
  | { kind: "short-utterance" }
  | { kind: "stt-ws-failed" }
  // 送ったのに確定文が返らなかった (A2)。`stt-ws-failed` と別に持つのは、あちらが
  // 「繋がらなかった」= 送る前に分かる失敗で、こちらは**送った後に黙って消える**もの。
  // 画面の出し方は同じでよいが、計測では分けたい (前者は 0 件、後者が 7 件だった)。
  | { kind: "stt-no-final" }
  | { kind: "mic-lost" };

/**
 * 確定文を待つ上限 (A2)。これを過ぎたら「聞き取れなかった」として聞き直す。
 *
 * **早すぎるほうが害が大きい** — 実際には届いていた回答に対して「もう一度」と言うと、
 * ユーザーは同じことを 2 回話す。実測の確定文レイテンシは p50 862ms / p95 1.6 秒で、
 * 60 秒級の発話でも 1 通目は 2.7 秒で返っている (2026-08-18)。その 5 倍の余裕を取る。
 *
 * 待っている間は処理中の合図音が鳴っているので、無言で放置されるわけではない。
 */
const FINAL_TIMEOUT_MS = 8000;

type Opts = {
  enabled: boolean;
  keytermsForCurrentQ: string[];
  // 質問タイプ別の発話終了判定 (spec 2026-07-18 §5.5): VAD の redemption (末尾無音)
  // を上書きする。未指定なら VadController の default (800ms) を使う。
  redemptionMs?: number;
  /**
   * 確定文が届いた。**`meta.continuation` は素通しする** (2026-08-15) —
   * ここで連結しないのは、着地先の設問と turnId を知っているのが ChatScreen だけだから。
   * 連結して待つと、着地後に届いた断片を正しい設問へ戻せなくなる。
   */
  onTranscriptCommitted: (text: string, meta?: { continuation: boolean }) => void;
  onPartial: (text: string) => void;
  onSilenceEscalation: (level: number) => void;
  onError: (e: ContinuousModeError) => void;
  // 発話 audio の R2 保存用 hook。loopback (TTS 再生中) と short-utterance の
  // 除外を通過した「Scribe に送るのと同じ Float32Array (16 kHz mono)」を渡す。
  // 保存自体は caller (ChatScreen) が WAV 化して fire-and-forget で enqueue する。
  onUtteranceAudio?: (audio: Float32Array) => void;
  // spec §6.2 #14/#15: barge-in pair の payload に含める ttsMessageId の取得元。
  // TtsPlayer クラス自体には (2026-07-11 時点) currentMessageId が無いため、
  // ChatScreen 側が「今再生中の message id」を追跡するラッパを渡す任意 hook として
  // 定義する。渡されない/undefined を返す場合は ttsMessageId なしで emit する。
  ttsPlayer: Pick<TtsPlayer, "isPlaying" | "stop"> & {
    currentMessageId?: () => string | undefined;
    // 保持のタイムアウトを測り直す (spec 2026-08-04 D4/D5)。保持は発話**開始**の
    // 200ms 後に始まるが、判定の待ち時間 (STT 確定 + 構造化 LLM) が始まるのは
    // 話し終えた瞬間なので、発話の長さぶん予算を食い潰していた。
    extendHold?: () => void;
  };
};

export function useContinuousMode(opts: Opts) {
  const vadRef = useRef<VadController | null>(null);
  const scribeRef = useRef<StreamingSttClient | null>(null);
  const silenceRef = useRef<SilenceTimer | null>(null);
  const [vadState, setVadState] = useState<VadUiState>("idle");

  // Bug B (stale closure, 2026-07-10) fix — latest ref pattern。
  // StreamingScribeClient / SilenceTimer / VadController は constructor 時点で
  // 受け取った callback を焼き込むため、opts が rerender で差し替わっても
  // 内部 pipeline は「初回 start() 時の callback」を呼び続けてしまう。
  // ChatScreen 側は phase / activeIdx / survey を capture した handleAnswer /
  // onError / onSilenceEscalation を毎 render で作り直すので、stale closure に
  // 落ちると phase 遷移が伝播せず古い state を触るバグになる (Bug B)。
  // 各 render で optsRef.current を最新の opts に差し替え、start() で構築する
  // ラッパは全て optsRef.current 経由で参照するようにする。これにより
  // instance を再生成せずに callback だけ「最新版」に追従させられる。
  const optsRef = useRef<Opts>(opts);
  optsRef.current = opts;

  // 「AI が読み上げている間は無音を数えない」という依頼そのもの (spec 2026-08-04 D1)。
  //
  // pauseSilence / resumeSilence は 2026-07-09 からあるが、依頼先の SilenceTimer が
  // **まだ生成されていない間に来た依頼は握り潰されていた**。本編突入時は挨拶の読み上げと
  // start() が同時に走り、読み上げの方が必ず先に pauseSilence() を呼ぶ (start は mic 許可 +
  // WS 接続 + VAD の onnx ロードで 1〜2 秒かかる)。その結果 start() の末尾が読み上げ中に
  // 10 秒のカウントを張り、挨拶 (実測 14.5 秒) の途中で「聞こえていますでしょうか」が
  // 出ていた (2026-08-04 の本番 3 セッションで実測、チケット参照)。
  //
  // 依頼を旗として保持し、**タイマーを張る側が全員この旗を見る**形にすれば、
  // 起動の完了順に関わらず依頼が消えない。
  //
  // 旗が表しているのは「読み上げが 1 件、進行中である」— useNarration.speakOne が
  // 冒頭で pauseSilence()、finally で resumeSilence() を呼ぶので、合成中・再生中・
  // 保持中のすべてを含む。ttsPlayer.isPlaying() で代用してはならない (合成中は false を
  // 返すため、同じ穴が「読み上げの合間」として残る)。
  const silencePausedRef = useRef(false);

  /**
   * **今ユーザーが話している最中か** (2026-08-11)。
   *
   * 無音の計測を張ってよくない状況が 2 つあり、旗も 2 つ要る —
   * 「AI が読み上げている」(`silencePausedRef`) と「ユーザーが話している」(これ)。
   * 後者が無いと、割り込みで話し始めた人が話し終える前に読み上げが終わったとき、
   * `resumeSilence()` が**まだ話しているのに 10 秒を張り直す** (実測で 3 度目の再現)。
   *
   * **降ろす経路は 2 つとも押さえてある** — `onSpeechEnd` と `onVADMisfire`。
   * vad-web は SpeechStart の後に必ずどちらかを呼ぶので、旗が立ったまま残って
   * 無音の再依頼が永久に出なくなる (原則 5 の出口が死ぬ) ことはない。
   * 起動・停止でも降ろす (VAD を作り直すと発話の途中経過は失われるため)。
   */
  const speakingRef = useRef(false);
  // 確定文を待つ網 (A2)。commit を送った時点で張り、確定文が 1 通でも届いたら解く。
  // **タイマーを 1 本しか持たない** — 発話は直列 (VAD が 1 区間ずつ渡す) なので、
  // 複数持つと解く相手を間違える余地だけが増える。
  const finalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * 無音の計測を始める。**張ってよいのは、AI も人も話していないときだけ。**
   * level をリセットする start() 側。
   */
  const clearFinalTimer = useCallback(() => {
    if (finalTimerRef.current !== null) {
      clearTimeout(finalTimerRef.current);
      finalTimerRef.current = null;
    }
  }, []);

  /**
   * commit の直後に張る。時間内に確定文が来なければ「聞き取れなかった」を出す (A2)。
   *
   * これが無いと、**送った後にサーバが切った**場合に画面にも音声にも何も出ない
   * (パイロットで消えた 22 件のうち 7 件がこれ)。ユーザーには「話したのに
   * 無かったことになった」としか見えない。
   */
  const armFinalTimer = useCallback(() => {
    clearFinalTimer();
    finalTimerRef.current = setTimeout(() => {
      finalTimerRef.current = null;
      dlog("final-timeout", { waitedMs: FINAL_TIMEOUT_MS });
      // **R2 に残す。** これが無いと「消えた発話が何件あって、そのうち何件を
      // 聞き直せたか」を後から突き合わせられない (A2 の受け入れ条件がそれ)。
      // `vad.speech_end` はあるのに `scribe.final` が無い件数 = このイベントの件数、
      // になっていることを次回ログで確かめる。
      const sid = useChatStore.getState().sessionId;
      if (sid) {
        eventBus.emit(
          makeEvent("scribe.final_timeout", sid, { tsMs: 0, waitedMs: FINAL_TIMEOUT_MS })
        );
      }
      optsRef.current.onError({ kind: "stt-no-final" });
    }, FINAL_TIMEOUT_MS);
  }, [clearFinalTimer]);

  const armSilence = useCallback((timer: SilenceTimer | null) => {
    if (!timer || silencePausedRef.current || speakingRef.current) return;
    timer.start();
  }, []);

  // Bug A (keyterms phase gate, 2026-07-10 Round 2) fix — auto-reconnect。
  // Scribe v2 Realtime WSS は connect 時 (query parameter) に keyterms を固定する。
  // phase 遷移で ChatScreen 側の keytermsForCurrentQ が変わったら、mic は維持したまま
  // 旧 WSS を close して新 keyterms 付きで reconnect する必要がある。
  // useEffect の deps に「値ベースの key」(join した文字列) を渡すことで、
  // ChatScreen が毎 render 新 array を返しても、値が同じなら React が deps 変化なしと
  // 判定して useEffect を再実行しない (identity 誤検知の regression 回避)。
  // generationRef は race 対策: 連続 reconnect で古い connect() が遅れて resolve した場合、
  // scribeRef 差し替え後の Scribe と衝突させないため世代番号で古い側をキャンセルする。
  const generationRef = useRef(0);
  const keytermsKey = opts.keytermsForCurrentQ.join("\x00");

  // 最後に受け取った turn 文脈。STT client は再接続で作り直される (ensureScribeConnected) ため、
  // 呼び出し側が 1 回呼ぶだけでは足りず、client を作るたびにここから適用する必要がある
  // (spec 2026-07-30 ターン文脈 §4.2)。抜けると「ターンの後半の発話だけ turnId が付かない」。
  const turnContextRef = useRef<{ turnIndex: number; turnId: string } | null>(null);

  // STT client の構築を 1 箇所に集約 (start / keyterms reconnect / idle 復帰で共用)。
  // プロバイダの選択は makeSttClient (sttClientFactory) に委譲し、この hook は知らない。
  // setTurnContext は共通インターフェース StreamingSttClient の一部なので、
  // どちらのプロバイダで作られた client にも同じように turn 文脈が適用される。
  const makeScribe = useCallback(() => {
    const scribe = makeSttClient({
      filterBackgroundAudio: readFilterBackgroundAudioFromEnv(),
      onPartial: (text) => optsRef.current.onPartial(text),
      onCommitted: (text, meta) => {
        // 1 通でも返れば「聞こえている」ことは分かる。割れた続き (continuation) を
        // 待って聞き直すと、既に受理した回答に対して「もう一度」と言うことになる。
        clearFinalTimer();
        optsRef.current.onTranscriptCommitted(text, meta);
      },
      onError: () => optsRef.current.onError({ kind: "stt-ws-failed" }),
    });
    const ctx = turnContextRef.current;
    if (ctx) scribe.setTurnContext({ ...ctx, utteranceIndex: 0 });
    return scribe;
  }, [clearFinalTimer]);

  // STT client を捨てる。閉じた時点で、そこへ commit 済みの発話の確定文は**もう届かない**
  // (閉じるのは意図的 — 遅れて届いた古い確定文が新しい発話の判定に食い込むため、
  // spec 2026-07-28 §3.6(1))。呼び出し側は確定文の到着でしか途中経過 (live captions) を
  // 消せないので、閉じたことを伝えないと画面に残り続ける。描画位置はスレッド末尾・
  // questionId は現在の設問なので、**設問が進むたびに新しい設問の下へ付いてくる**
  // (実機で「入力していないのにゴーストが出る」として報告)。
  // close する経路は 2 つ (idle 復帰 / keyterms 変更) あり、片方だけ塞ぐと設問構成が
  // 変わった途端に同じゴーストが戻るので、両方をこの 1 本に通す。
  const discardScribe = useCallback((client: StreamingSttClient | null | undefined) => {
    if (!client) return;
    client.close();
    optsRef.current.onPartial("");
  }, []);

  // idle 復帰 (2026-07-21 実機で判明): continuous の長寿命 WS は TTS 読み上げ中などの
  // 無音で ElevenLabs 側にアイドル切断され、次の発話の sendAudio/commit が
  // readyState!=OPEN で黙って捨てられていた (氏名認識は「1 発話=1 WS」で話す直前に開くため
  // 無縁だった)。発話の送信直前 (onSpeechEnd) と先行 (onSpeechStart) で WS の開通を保証し、
  // 閉じていれば張り直す。connect 中の重複張り直しは scribeConnectingRef で 1 本化する。
  // 起動中の start()。in-flight を 1 本に畳んで、連打で pipeline が二重に立つのを防ぐ
  // (下の start() の冒頭を参照)。
  const startingRef = useRef<Promise<void> | null>(null);

  const scribeConnectingRef = useRef<Promise<StreamingSttClient> | null>(null);
  const ensureScribeConnected = useCallback(async (): Promise<StreamingSttClient> => {
    const current = scribeRef.current;
    if (current && current.isOpen()) return current;
    if (scribeConnectingRef.current) return scribeConnectingRef.current;
    const p = (async () => {
      // 旧 client を閉じてから差し替える。閉じないと、遅れて届いた古い確定文が
      // 新しい発話の判定に食い込む (spec 2026-07-28 §3.6(1))。読み上げ中の
      // 回り込みでこの経路を通る頻度が上がるため、明示的に閉じる。
      discardScribe(current);
      const scribe = makeScribe();
      scribeRef.current = scribe;
      await scribe.connect(optsRef.current.keytermsForCurrentQ);
      return scribe;
    })();
    scribeConnectingRef.current = p;
    try {
      return await p;
    } finally {
      scribeConnectingRef.current = null;
    }
  }, [makeScribe, discardScribe]);

  const start = useCallback(async () => {
    if (!optsRef.current.enabled) return;
    // 起動中なら同じ promise を待たせる。連打や「自動起動 + 手動タップ」の競合で
    // 複数の起動が並行すると、後から来たほうが前の ref を上書きして
    // 前のインスタンスが孤児になる (ensureScribeConnected の scribeConnectingRef と同じ作法)。
    if (startingRef.current) return startingRef.current;
    // 既に動いているなら何もしない。**判定は VAD の生死だけを見る。**
    // ref が非 null かどうかで見ると、idle 切断 (WS だけ閉じている状態) のたびに
    // VAD ごと作り直すことになる — WS の復旧は ensureScribeConnected の担当で、
    // 発話の直前に張り直される。
    if (vadRef.current?.isRunning()) return;

    const p = (async () => {
      // 作り直しに入る前に、前回の残骸を明示的に片付ける。
      // 上書きするだけだと旧インスタンスを誰も止められなくなり、
      // 孤児化した SilenceTimer は完了画面でも「聞こえていますでしょうか」を積み、
      // 旧 VAD は同じ MediaStream を解析し続けて 1 発話で commit を 2 回撃つ。
      // 旗も降ろす。VAD を捨てる以上、**進行中だった発話の onSpeechEnd はもう来ない**。
      // 残すと、この後の armSilence が素通りして無音の再依頼が二度と出なくなる。
      // 到達経路は例外ではない — マイクのトラック切断 (mic.stream_ended {track_ended})
      // は社内パイロットで 5 件あり、そこからの再タップはアプリ自身が silence2 で促す。
      speakingRef.current = false;
      silenceRef.current?.stop();
      vadRef.current?.destroy();
      scribeRef.current?.close();
      silenceRef.current = null;
      vadRef.current = null;
      scribeRef.current = null;

      const stream = await mediaStreamManager.ensureStream();

      const scribe = makeScribe();
      try {
        await scribe.connect(optsRef.current.keytermsForCurrentQ);
      } catch (e) {
        // connect に失敗した client は scribeRef に入らないので、上の後片付けでも
        // 下の catch でも拾えない。その場で閉じないと WS が leak する。
        scribe.close();
        throw e;
      }
      scribeRef.current = scribe;

      const silence = new SilenceTimer({
        escalations: [10000, 10000, 10000],
        onEscalation: (level) => optsRef.current.onSilenceEscalation(level),
      });
      silenceRef.current = silence;

      const vad = new VadController({
        redemptionMs: optsRef.current.redemptionMs,
        onSpeechStart: () => {
          // 2026-07-09 preview で判明した loopback 対策:
          // AI TTS が speaker → mic で拾われて VAD が発火するケースが多発。
          // silence.reset() と vadState="hearing" は user 発話確定後に行う。
          // TTS ループバックで silence を誤リセットすると無音時の再依頼が
          // 永久に出なくなる (実質 escalation が動かない bug になる)。
          //
          // 2026-07-28 D11: TTS 再生中はここで読み上げを止めない。vad-web の
          // SpeechStart はしきい値超えの最初の 1 フレーム (16 kHz / 512 samples = 32ms)
          // で発火するので、咳・紙をめくる音・スピーカーからの回り込みでも鳴る。
          // 読み上げを止めるかどうかは 200ms 続いてから (onSpeechRealStart) 判断する。
          const ttsPlaying = optsRef.current.ttsPlayer.isPlaying();
          dlog("speech-start", { ttsPlaying });
          // **読み上げ中かどうかに関わらず、無音の計測は止める** (2026-08-11、
          // 実 VAD の E2E が捕まえた穴)。ここが早期 return の後ろにあると、
          // 読み上げ中に話し始めた (barge-in) 場合だけ計測が止まらず、
          // **発話の最中に「聞こえていますでしょうか」が割り込む** —
          // 直したはずの症状がこの経路から再現していた (実測: 発話開始の 9.62 秒後)。
          //
          // 止めて安全な根拠: 張り直しは onSpeechEnd / onVADMisfire が armSilence 経由で
          // 行い、vad-web は SpeechStart の後に必ずどちらかを呼ぶ (**misfire を配線した
          // から成立する**)。ループバック (AI 自身の声) で止まっても、読み上げ中は
          // armSilence が旗を見て張り直さないので、読み上げが終わる時点の
          // resumeSilence で正しく再開する。
          speakingRef.current = true;
          silence.reset();
          if (ttsPlaying) return;
          setVadState("hearing");
          // 発話中に WS を先行して開き直しておく (idle 切断からの復帰レイテンシを隠す)。
          void ensureScribeConnected().catch(() => {
            /* onSpeechEnd 側の ensureScribeConnected で再試行・エラー通知するので握りつぶす */
          });
        },
        onSpeechRealStart: () => {
          // 発話が 200ms (minSpeechMs) 続いた。TTS 再生中ならここで初めて読み上げを
          // 止める (spec 2026-07-28 D11)。呼び出し側の stop() は「破棄」ではなく
          // 「一時停止して判定へ」を意味する (spec §3.6(1))。
          if (!optsRef.current.ttsPlayer.isPlaying()) return;
          dlog("speech-real-start", { ttsPlaying: true });
          // spec 2026-07-11 §6.2 #14/#15: barge-in pair。sessionId は hook の Opts
          // ではなく useChatStore から解決する (ChatScreen が session 開始時に
          // startSession({sessionId}) 済の前提、他 emit 挿入点と同じ pattern)。
          // sessionId 未設定 (observability 未初期化) でも停止自体は
          // voice-first のため必ず実行する。
          const ttsMessageId = optsRef.current.ttsPlayer.currentMessageId?.();
          const detectedAt = performance.now();
          const sessionId = useChatStore.getState().sessionId;
          if (sessionId) {
            eventBus.emit(makeEvent("bargein.detected", sessionId, { ttsMessageId }));
          }
          optsRef.current.ttsPlayer.stop();
          const cancelLatencyMs = Math.round(performance.now() - detectedAt);
          if (sessionId) {
            eventBus.emit(
              makeEvent("bargein.tts_cancelled", sessionId, {
                ttsMessageId,
                cancelLatencyMs,
              })
            );
          }
          // 保持した発話はそのまま判定材料になるので、WS を先行して開いておく。
          // onSpeechStart 側の先行オープンはこの経路を通らないため、ここで補う。
          void ensureScribeConnected().catch(() => {
            /* onSpeechEnd 側の ensureScribeConnected で再試行・エラー通知するので握りつぶす */
          });
        },
        onVADMisfire: () => {
          // 発話が minSpeechMs (200ms) に届かず打ち切られた。**この経路では
          // onSpeechEnd が呼ばれない**ので、ここで張り直さないと無音の計測が
          // 二度と始まらない — onSpeechStart の silence.reset() が計測を
          // 止めるようになったため (2026-08-05 のパイロット §2.3)。
          // 止まったままだと level 1 のテキスト退避 (原則 5 の出口) に
          // 永久に到達しない。armSilence 経由なので読み上げ中は張らない
          // (#62 で塞いだ「読み上げ中に数える」穴をこの経路から開け直さない)。
          dlog("vad-misfire");
          speakingRef.current = false;
          setVadState("listening");
          armSilence(silence);
        },
        onSpeechEnd: (audio) => {
          // **どの分岐へ進んでも必ず降ろす。** 下は TTS 再生中・短すぎる発話・通常経路の
          // 3 つに分かれ、早期 return もあるので、旗を降ろすのは分岐より前でなければ
          // 取りこぼす (残ると無音の再依頼が二度と出ない = 原則 5 の出口が死ぬ)。
          speakingRef.current = false;
          // TTS 中の onSpeechEnd は Scribe に送らない (loopback で AI 発話が
          // transcript に混ざる問題を防止)。onSpeechStart で TTS.stop() を呼んで
          // isPlaying=false になっている場合はこの分岐に入らない。
          if (optsRef.current.ttsPlayer.isPlaying()) {
            dlog("speech-end-dropped", { reason: "tts-playing", samples: audio.length });
            return;
          }
          if (audio.length < SHORT_UTTERANCE_SAMPLES_16K) {
            dlog("speech-end-dropped", { reason: "short-utterance", samples: audio.length });
            // spec R3.3.2 short-utterance recovery:
            // < 500ms (16kHz なら 8000 samples 未満) は STT を skip して
            // 「うまく聞き取れませんでした」recovery route に投げる。
            optsRef.current.onError({ kind: "short-utterance" });
            setVadState("listening");
            armSilence(silence);
            return;
          }
          // 発話 audio の R2 保存: Scribe に送る直前の PCM を caller に渡す
          // (voice-first: 保存側は fire-and-forget、STT pipeline はブロックしない)。
          optsRef.current.onUtteranceAudio?.(audio);
          setVadState("processing");
          armSilence(silence);
          // 判定の待ち時間はここから始まる。読み上げを保持したまま来た発話なら、
          // 保持のタイムアウトをこの時点から測り直す (spec 2026-08-04 D5)。
          // measure し直さないと、長い回答ほど「喋っている時間」に予算を食われ、
          // 確定文が届く前後で必ず時間切れになる。
          optsRef.current.ttsPlayer.extendHold?.();
          // 通常経路: open な WS へ即送信 (同期)。scribeRef 経由なので keyterms 変化で
          // reconnect されても常に「今 open な最新 Scribe」に送る (旧 Bug A fix の意図を維持)。
          // idle 切断で閉じていた場合のみ張り直してから送る (2026-07-21 実機対策)。
          const scribe = scribeRef.current;
          if (scribe && scribe.isOpen()) {
            dlog("speech-end-sent", { samples: audio.length, wsOpen: true });
            scribe.sendAudio(audio);
            scribe.commit();
            armFinalTimer();
          } else {
            dlog("speech-end-reconnecting", { samples: audio.length, wsOpen: false });
            void ensureScribeConnected().then(
              (reconnected) => {
                dlog("speech-end-sent", { samples: audio.length, afterReconnect: true });
                reconnected.sendAudio(audio);
                reconnected.commit();
                armFinalTimer();
              },
              () => {
                dlog("speech-end-dropped", { reason: "ws-reconnect-failed" });
                // ここでは網を張らない — 既に案内を出しているので、二重に聞き直すと
                // 「もう一度」が 2 回続く。
                optsRef.current.onError({ kind: "stt-ws-failed" });
              }
            );
          }
        },
      });
      try {
        await vad.start(stream);
      } catch (e) {
        // 途中まで作った分を残さない。残すと、エラー表示からの正規のリトライタップで
        // 旧 WS が leak する (上のガードは VAD の生死しか見ないのでここを通り抜ける)。
        scribe.close();
        silence.stop();
        scribeRef.current = null;
        silenceRef.current = null;
        throw e;
      }
      vadRef.current = vad;
      armSilence(silence);
      setVadState("listening");
    })();

    startingRef.current = p;
    try {
      await p;
    } finally {
      startingRef.current = null;
    }
  }, [makeScribe, ensureScribeConnected, armSilence, armFinalTimer]);

  const pause = useCallback(() => {
    vadRef.current?.pause();
    mediaStreamManager.pause();
    silenceRef.current?.stop();
    setVadState("idle");
  }, []);

  const resume = useCallback(() => {
    mediaStreamManager.resume();
    vadRef.current?.resume();
    // 処理中に止めたマイクを戻す経路 (useProcessingCue)。ここで無条件に張ると、
    // お礼 + 次の設問を読んでいる間にカウントが進み、ユーザーが考える時間が
    // 実質 3〜5 秒に縮む (実測: 発話終了の 11.4〜13.0 秒後に発火していた)。
    armSilence(silenceRef.current);
    setVadState("listening");
  }, [armSilence]);

  const stop = useCallback(() => {
    // 旗を降ろす。VAD を捨てる以上、**この発話の onSpeechEnd はもう来ない** ので、
    // 残すと次に起動しても armSilence が素通りし、無音の再依頼が二度と出なくなる。
    speakingRef.current = false;
    // 確定文を待つ網も解く (A2)。残すと **完了画面で「もう一度お話しください」が出る** —
    // 送信の直前に話した発話の網が、マイクを止めた後に発火する。
    clearFinalTimer();
    silenceRef.current?.stop();
    vadRef.current?.destroy();
    scribeRef.current?.close();
    mediaStreamManager.stop();
    vadRef.current = null;
    scribeRef.current = null;
    silenceRef.current = null;
    setVadState("idle");
  }, [clearFinalTimer]);

  // 2026-07-09 preview で判明した「TTS 再生中も silence timer が進んで
  // 無音時の再依頼が AI 発話中に出る」問題への対策。
  // ChatScreen が TTS 再生の前後 (playTts) でこの 2 つを呼び、
  // AI が話している間は escalation を止めておく。
  // stop() ではなく pause() のような明示的 API にしたのは、start() が
  // level を 0 リセットする副作用があり、TTS 中断のたびにリセットが走ると
  // ユーザ側の無音カウントも巻き戻ってしまうため。
  const pauseSilence = useCallback(() => {
    // 旗を先に立てる。この時点でタイマーがまだ無くても (本編突入時の競合)、
    // 後から張ろうとする側が armSilence でここを見る (spec 2026-08-04 D1)。
    silencePausedRef.current = true;
    silenceRef.current?.stop();
  }, []);
  const resumeSilence = useCallback(() => {
    // start() ではなく resume(): start() は level を 0 にリセットするため、AI の読み上げが
    // 終わるたびに無音カウントが巻き戻り、「聞こえていますでしょうか」(level 0) が無限に
    // 繰り返されて level 1 の「マイクが届いていないかもしれません + テキスト入力を強制
    // expand」に永久に到達しなかった (2026-07-25 実機。voice-first 原則 5 の fallback が
    // 機能しない)。上のコメントが元々意図していた「level を保持したまま再開」を満たす。
    silencePausedRef.current = false;
    // **話している最中なら張り直さない。** ここで無条件に resume() すると、
    // 割り込みで話し始めた人がまだ話しているのに 10 秒が走り出す (2026-08-11 実測)。
    // 張り直しは話し終わった時点の onSpeechEnd / onVADMisfire が行う。
    if (speakingRef.current) return;
    silenceRef.current?.resume();
  }, []);

  // ターンが変わったことを伝える。以後この文脈が、再接続で作り直される Scribe にも適用される
  // (turnContextRef 経由で makeScribe が毎回参照するため)。
  const setTurnContext = useCallback((ctx: { turnIndex: number; turnId: string }) => {
    turnContextRef.current = ctx;
    scribeRef.current?.setTurnContext({ ...ctx, utteranceIndex: 0 });
  }, []);

  // Bug A auto-reconnect (2026-07-10 Round 2): keytermsKey 変化で reconnect。
  // deps は「値ベースの文字列 key」なので、ChatScreen が毎 render 新 array を返しても
  // 値が同じなら effect は再実行されない (identity 誤検知回避)。start() 未実行なら
  // scribeRef=null で早期 return (enabled=false のときも start() が noop → scribeRef=null)。
  useEffect(() => {
    if (!scribeRef.current) return;
    const oldScribe = scribeRef.current;
    const generation = ++generationRef.current;
    discardScribe(oldScribe);
    const scribe = makeScribe();
    scribeRef.current = scribe;
    void scribe.connect(optsRef.current.keytermsForCurrentQ).then(
      () => {
        // resolve 時点で自分より新しい reconnect が走っていたら、この scribe は stale。
        // Race で scribeRef を上書きしないよう old-side を close するだけ (scribeRef は
        // 差し替え済み)。mock 環境 (connect が即 resolve) では通常このガードは通らない。
        if (generation !== generationRef.current) {
          scribe.close();
        }
      },
      () => {
        // connect() 失敗は StreamingScribeClient 内部で onError → opts.onError({kind:"stt-ws-failed"})
        // が既に発火するため、ここでは追加通知しない (二重通知回避)。
      }
    );
  }, [keytermsKey, makeScribe, discardScribe]);

  // 質問タイプ別の発話終了判定 (spec 2026-07-18 §5.5): redemption を動的に切り替える。
  // setOptions は同期なので Scribe keyterms のような reconnect / generation race 対策は不要。
  const redemptionMs = opts.redemptionMs;
  useEffect(() => {
    if (redemptionMs === undefined) return;
    vadRef.current?.setRedemptionMs(redemptionMs);
  }, [redemptionMs]);

  // cleanup on unmount
  useEffect(() => () => stop(), [stop]);

  return { start, pause, resume, stop, pauseSilence, resumeSilence, vadState, setTurnContext };
}
